'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import cn from 'classnames';

import { Button, Input, Select, TextArea, Checkbox } from '@components/Form';
import { ExpandableText } from '@components/Form/Checkbox';
import { logger } from '@logger';

interface FormField {
  name: string;
  label: string;
  type: 'input' | 'select' | 'textarea' | 'email' | 'agreement' | 'rodo' | 'text';
  required: boolean;
  options?: { [key: string]: string };
  partner?: string;
  textType?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'regular' | 'small';
}

interface FormBuilderProps {
  shortcode: string;
  onSubmit?: (data: any) => void;
}

const Wrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="mx-auto w-full lg:w-[420px]">{children}</div>
);

export default function FormBuilder({ shortcode, onSubmit }: FormBuilderProps) {
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>({});

  const t = useTranslations('formBuilder');

  // Get predefined label from translations
  const getPredefinedLabel = (fieldName: string): string => {
    try {
      const labelKey = `labels.${fieldName}` as any;
      return t(labelKey);
    } catch {
      return fieldName; // fallback to fieldName if translation doesn't exist
    }
  };

  // Parse shortcode to extract form fields
  const parseShortcode = (shortcode: string): FormField[] => {
    // Remove [form and ] brackets
    const content = shortcode.replaceAll(/[”″]/g, '"');

    if (!content) return [];

    const fields: FormField[] = [];

    // Updated regex to handle both text fields (h1:"text") and regular fields
    const re = /(^|\s)((h[1-6]|regular|small):"([^"]+)"|([A-Za-z0-9_-]+)(\*)?(:"([^"]+)")?)/g;

    const matches = Array.from(content.matchAll(re));

    if (!matches || matches.length === 0) return [];

    for (const match of matches) {
      const field: FormField = {
        name: '',
        label: '',
        type: 'input',
        required: false,
      };

      // Check if it's a text field (h1:"text", regular:"text", etc.)
      if (match[3] && match[4]) {
        // Text field format: h1:"text"
        field.name = match[3];
        field.label = match[4];
        field.type = 'text';
        field.textType = match[3] as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'regular' | 'small';
      } else if (match[5]) {
        // Regular field format: fieldname* or fieldname:"label"
        field.name = match[5];
        field.required = match[6] === '*';

        if (match[8]) {
          // Has custom label: fieldname:"label"
          field.label = match[8];
        } else {
          // No custom label, use predefined or generate
          field.label = getPredefinedLabel(field.name) || field.name.replace(/_/g, ' ');
        }

        // Set field types based on field name
        if (field.name === 'email') {
          field.type = 'email';
        } else if (field.name === 'rodo') {
          field.type = 'rodo';
        } else if (
          field.name.includes('agreement') ||
          field.name === 'rules' ||
          field.name === 'policy' ||
          field.name === 'marketing' ||
          field.name === 'newsletter'
        ) {
          field.type = 'agreement';
          if (field.name === 'agreement_partner') {
            field.partner = field.label;
          }
        }
      }

      // Use predefined label or capitalize field name if label is still empty
      if (!field.label) {
        field.label =
          getPredefinedLabel(field.name) ||
          field.name
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
      }

      fields.push(field);
    }

    return fields;
  };

  const fields = parseShortcode(shortcode);

  const handleInputChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset field errors
    setFieldErrors({});

    // Validate required fields (exclude text fields which are display-only)
    const missingFields = fields
      .filter((field) => field.type !== 'text' && field.required && !formData[field.name])
      .map((field) => field.label);

    if (missingFields.length > 0) {
      // Mark missing fields as errors
      const newFieldErrors: { [key: string]: boolean } = {};
      fields.forEach((field) => {
        if (field.type !== 'text' && field.required && !formData[field.name]) {
          newFieldErrors[field.name] = true;
        }
      });
      setFieldErrors(newFieldErrors);
      setSubmitMessage(t('messages.missingFields', { fields: missingFields.join(', ') }));
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('/api/form-submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formData,
          fields,
          shortcode,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitMessage(t('messages.success'));
        setFormData({});
        if (onSubmit) {
          onSubmit(formData);
        }
      } else {
        setSubmitMessage(result.error || t('messages.error'));
      }
    } catch (error) {
      logger.error(`Form submission error: ${JSON.stringify(error)}`);
      setSubmitMessage(t('messages.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (fields.length === 0) {
    return (
      <div className="bg-red-50 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-md border border-red-500 p-4 text-center">
        {t('messages.parseError')}
      </div>
    );
  }

  const getAgreementText = (fieldName: string, partner?: string): { label: string; content: string } => {
    try {
      // Map field names to formBuilder agreement keys
      const agreementKeyMap = {
        rules: 'agreements.rules',
        policy: 'agreements.policy',
        marketing: 'agreements.marketing',
        newsletter: 'agreements.newsletter',
        agreement: 'agreements.marketing',
        agreement_partner: 'agreements.agreement_partner',
        rodo: 'agreements.rodo',
      };

      const formBuilderKey = agreementKeyMap[fieldName] || 'agreements.marketing';

      if (partner && fieldName === 'agreement_partner') {
        // For agreement_partner, we use the parameterized agreement_partner agreement
        return {
          label: t(`${formBuilderKey}.label`, { partner }),
          content: t(`${formBuilderKey}.contents`, { partner }),
        };
      }

      return {
        label: t(`${formBuilderKey}.label`),
        content: t.raw(`${formBuilderKey}.contents`),
      };
    } catch (error) {
      logger.warn(`Agreement text not found for ${fieldName}: ${JSON.stringify(error)}`);
      return {
        label: fieldName,
        content: fieldName,
      };
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-dark-500">
      <form onSubmit={handleSubmit} className="flex flex-col">
        {fields.map((field, index) => {
          const fieldContent = (
            <div
              className={cn('mb-2 w-full lg:mb-4', {
                'text-center': field.type === 'text',
                'text-left': field.type === 'agreement' || field.type === 'rodo',
              })}
            >
              {field.type === 'text' ? (
                <div className="mb-4">
                  {field.textType === 'h1' && (
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{field.label}</h1>
                  )}
                  {field.textType === 'h2' && (
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{field.label}</h2>
                  )}
                  {field.textType === 'h3' && (
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{field.label}</h3>
                  )}
                  {field.textType === 'h4' && (
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">{field.label}</h4>
                  )}
                  {field.textType === 'h5' && (
                    <h5 className="text-base font-bold text-gray-900 dark:text-white">{field.label}</h5>
                  )}
                  {field.textType === 'h6' && (
                    <h6 className="text-sm font-bold text-gray-900 dark:text-white">{field.label}</h6>
                  )}
                  {field.textType === 'regular' && (
                    <p className="text-base text-gray-900 dark:text-white">{field.label}</p>
                  )}
                  {field.textType === 'small' && (
                    <p className="text-sm text-gray-600 dark:text-gray-300">{field.label}</p>
                  )}
                  {(!field.textType ||
                    !['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'regular', 'small'].includes(field.textType)) && (
                    <p className="text-base text-gray-900 dark:text-white">{field.label}</p>
                  )}
                </div>
              ) : field.type === 'select' ? (
                <>
                  <label
                    htmlFor={field.name}
                    className="mb-1 block text-left text-xs font-medium text-gray-900 dark:text-white"
                  >
                    {field.label}
                    {field.required && <span className="ml-1 text-red-500">*</span>}
                  </label>
                  <Select
                    id={field.name}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                    error={fieldErrors[field.name]}
                    placeholder={t('placeholders.selectOption')}
                    className="mb-1"
                    options={
                      field.options
                        ? Object.entries(field.options).map(([value, label]) => ({
                            value,
                            label,
                          }))
                        : []
                    }
                  />
                  {fieldErrors[field.name] && (
                    <p className="text-xs text-red-500">{t('messages.missingFields', { fields: field.label })}</p>
                  )}
                </>
              ) : field.type === 'textarea' ? (
                <>
                  <label
                    htmlFor={field.name}
                    className="mb-1 block text-left text-xs font-medium text-gray-900 dark:text-white"
                  >
                    {field.label}
                    {field.required && <span className="ml-1 text-red-500">*</span>}
                  </label>
                  <TextArea
                    id={field.name}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                    error={fieldErrors[field.name]}
                    rows={4}
                    placeholder={field.label}
                    className="mb-1"
                  />
                  {fieldErrors[field.name] && (
                    <p className="text-xs text-red-500">{t('messages.missingFields', { fields: field.label })}</p>
                  )}
                </>
              ) : field.type === 'agreement' ? (
                <>
                  <Checkbox
                    id={field.name}
                    name={field.name}
                    checked={formData[field.name] || false}
                    onChange={(e) => handleInputChange(field.name, e.target.checked)}
                    required={field.required}
                    error={fieldErrors[field.name]}
                    label={getAgreementText(field.name, field.partner).label}
                    tooltip={getAgreementText(field.name, field.partner).content}
                    wrapperClassName="mb-1 text-left"
                  />
                  {fieldErrors[field.name] && (
                    <p className="text-xs text-red-500">{t('messages.missingFields', { fields: field.label })}</p>
                  )}
                </>
              ) : field.type === 'rodo' ? (
                <>
                  <div className="mb-2 text-left">
                    <ExpandableText
                      text={getAgreementText(field.name).label}
                      expandedText={getAgreementText(field.name).content}
                      required={false}
                      error={false}
                    />
                  </div>
                </>
              ) : (
                <>
                  <label
                    htmlFor={field.name}
                    className="mb-1 block text-left text-xs font-medium text-gray-900 dark:text-white"
                  >
                    {field.label}
                    {field.required && <span className="ml-1 text-red-500">*</span>}
                  </label>
                  <Input
                    type={field.type === 'email' ? 'email' : 'text'}
                    id={field.name}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                    error={fieldErrors[field.name]}
                    placeholder={field.label}
                    className="mb-1"
                  />
                  {fieldErrors[field.name] && (
                    <p className="text-xs text-red-500">{t('messages.missingFields', { fields: field.label })}</p>
                  )}
                </>
              )}
            </div>
          );

          // For text fields, return content directly (full width)
          // For other fields, wrap with the Wrapper component
          return field.type === 'text' ? (
            <div key={index}>{fieldContent}</div>
          ) : (
            <Wrapper key={index}>{fieldContent}</Wrapper>
          );
        })}

        <div className="mb-4 flex w-full flex-row items-center justify-center lg:mb-8">
          <Button type="submit" disabled={isSubmitting} loading={isSubmitting}>
            {isSubmitting ? t('buttons.submitting') : t('buttons.submit')}
          </Button>
        </div>

        {submitMessage && (
          <div
            className={cn(
              'w-full rounded-md p-3 text-center text-sm',
              submitMessage === t('messages.success')
                ? 'bg-green-50 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200 border'
                : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200 border',
            )}
          >
            {submitMessage}
          </div>
        )}
      </form>
    </div>
  );
}
